import { InfoCircleOutlined, TeamOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Badge, Input, Progress, Space, Table, Tag, Tooltip, Typography } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SalesPerformance } from '../../../types/dashboard';
import { formatNumber, formatPercent, formatRupiah, getProgressColor, glassTableContainerStyle } from '../utils';

const { Title, Text } = Typography;

interface SalesPerformanceTableProps {
  performanceBySales: SalesPerformance[];
  loading: boolean;
  page: number;
  limit: number;
  total: number;
  sortBy: string;
  sortOrder: string;
  onTableChange: (pagination: any, filters: any, sorter: any) => void;
  onSearch: (value: string) => void;
}

export const SalesPerformanceTable: React.FC<SalesPerformanceTableProps> = ({
  performanceBySales,
  loading,
  page,
  limit,
  total,
  sortBy,
  sortOrder,
  onTableChange,
  onSearch
}) => {
  const navigate = useNavigate();

  // Map antd sorting order to desc/asc or undefined
  const getAntdSortOrder = (colKey: string) => {
    if (sortBy === colKey) {
      return sortOrder === 'asc' ? 'ascend' as const : 'descend' as const;
    }
    return undefined;
  };

  // Generate a clean sorting label for the subtitle
  const getSortingLabel = () => {
    let colName = 'Nama Sales';
    if (sortBy === 'hot') colName = 'Jumlah Hot';
    if (sortBy === 'rsv') colName = 'Jumlah RSV';
    if (sortBy === 'ok') colName = 'Jumlah Closing (OK)';
    if (sortBy === 'omzet') colName = 'Realisasi Omzet';

    const direction = sortOrder === 'asc'
      ? (sortBy === 'name' ? 'A-Z' : 'Terendah')
      : (sortBy === 'name' ? 'Z-A' : 'Tertinggi');

    return `${colName} (${direction})`;
  };

  const salesColumns: TableProps<SalesPerformance>['columns'] = [
    {
      title: (
        <Text strong style={{ color: sortBy === 'name' ? '#1890ff' : 'inherit' }}>
          Sales Agent
        </Text>
      ),
      dataIndex: 'user_name',
      key: 'user_name',
      fixed: 'left' as const,
      width: 130,
      sorter: true,
      sortOrder: getAntdSortOrder('name'),
      showSorterTooltip: false,
      render: (text: string) => (
        <Space>
          <TeamOutlined style={{ color: '#1890ff' }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Kawasan',
      dataIndex: 'property_areas',
      key: 'property_areas',
      width: 150,
      align: 'center' as const,
      onCell: () => ({
        style: {
          whiteSpace: 'normal' as const,
          wordBreak: 'break-word' as const,
        },
      }),
      render: (areas: string[] | string) => {
        const areaList = Array.isArray(areas)
          ? areas
          : typeof areas === 'string' && areas
            ? (areas as string).split(',').map((s) => s.trim()).filter(Boolean)
            : [];

        if (areaList.length === 0) {
          return <Text type="secondary" italic>-</Text>;
        }

        return (
          <Space size={[0, 4]} wrap style={{ width: '100%', maxWidth: '100%' }}>
            {areaList.map((area) => (
              <Tag
                color="cyan"
                key={area}
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  height: 'auto',
                  lineHeight: '1.35',
                  padding: '2px 8px',
                  margin: '2px 0',
                  maxWidth: '100%',
                  display: 'inline-block',
                }}
              >
                {area}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Rencana',
      align: 'center' as const,
      children: [
        {
          title: (
            <Space size={4}>
              <span>Target Leads</span>
              <Tooltip title="Target jumlah perolehan calon pembeli (leads) yang dibebankan Kantor Pusat kepada individu sales agent pada periode yang difilter.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'leads_target',
          align: 'center' as const,
          width: 100,
          render: (record: SalesPerformance) => formatNumber(record.leads.target),
        },
      ]
    },
    {
      title: 'Realisasi',
      align: 'center' as const,
      children: [
        {
          title: (
            <Space size={4}>
              <span>Jumlah Leads</span>
              <Tooltip title="Jumlah leads aktif yang berhasil ditugaskan ke sales agent ini, beserta persentase pemenuhan terhadap target pribadinya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'leads_realization',
          align: 'center' as const,
          width: 100,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" align="end" size={0} style={{ width: '100%' }}>
              <Text strong>{formatNumber(record.leads.realization)}</Text>
              <Progress
                percent={record.leads.percentage}
                size="small"
                status={record.leads.percentage >= 100 ? "success" : "active"}
                strokeColor={getProgressColor(record.leads.percentage)}
                style={{ width: 80, margin: 0 }}
              />
            </Space>
          ),
        },
        {
          title: (
            <Space size={4}>
              <span>Cold</span>
              <Tooltip title="Jumlah calon pembeli aktif bersuhu 'Cold' (minat rendah/awal) yang saat ini ditangani oleh sales agent ini, beserta persentase porsinya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'detail_cold',
          align: 'center' as const,
          width: 100,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" size={0} align="center">
              <Text strong>{formatNumber(record.leads.detail_cold ?? 0)}</Text>
              <Tag color="blue" style={{ fontSize: '10px', margin: 0 }}>
                {formatPercent(record.leads.detail_cold_percentage ?? 0)}
              </Tag>
            </Space>
          ),
        },
        {
          title: (
            <Space size={4}>
              <Text strong style={{ color: sortBy === 'hot' ? '#1890ff' : 'inherit' }}>
                Hot
              </Text>
              <Tooltip title="Jumlah calon pembeli aktif bersuhu 'Hot' (sangat berminat/potensial tinggi) yang saat ini ditangani oleh sales agent ini, beserta persentase porsinya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'detail_hot',
          align: 'center' as const,
          width: 100,
          sorter: true,
          sortOrder: getAntdSortOrder('hot'),
          showSorterTooltip: false,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" size={0} align="center">
              <Text strong>{formatNumber(record.leads.detail_hot ?? 0)}</Text>
              <Tag color="volcano" style={{ fontSize: '10px', margin: 0 }}>
                {formatPercent(record.leads.detail_hot_percentage ?? 0)}
              </Tag>
            </Space>
          ),
        },
        {
          title: (
            <Space size={4}>
              <Text strong style={{ color: sortBy === 'rsv' ? '#1890ff' : 'inherit' }}>
                RSV
              </Text>
              <Tooltip title="Jumlah calon pembeli aktif bersuhu 'RSV' (berminat dan sedang proses negosiasi/survei) yang saat ini ditangani sales agent ini, beserta persentase porsinya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'detail_prospect',
          align: 'center' as const,
          width: 100,
          sorter: true,
          sortOrder: getAntdSortOrder('rsv'),
          showSorterTooltip: false,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" size={0} align="center">
              <Text strong>{formatNumber(record.leads.detail_prospect ?? 0)}</Text>
              <Tag color="orange" style={{ fontSize: '10px', margin: 0 }}>
                {formatPercent(record.leads.detail_prospect_percentage ?? 0)}
              </Tag>
            </Space>
          ),
        },
        {
          title: (
            <Space size={4}>
              <Text strong style={{ color: sortBy === 'ok' ? '#1890ff' : 'inherit' }}>
                Closing (OK)
              </Text>
              <Tooltip title="Jumlah transaksi pemesanan unit yang telah berhasil diselesaikan oleh sales agent ini dan sukses disetujui resmi oleh Kantor Pusat, beserta persentase porsinya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'detail_ok',
          align: 'center' as const,
          width: 100,
          sorter: true,
          sortOrder: getAntdSortOrder('ok'),
          showSorterTooltip: false,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" size={0} align="center">
              <Text strong>{formatNumber(record.leads.detail_ok ?? 0)}</Text>
              <Tag color="green" style={{ fontSize: '10px', margin: 0 }}>
                {formatPercent(record.leads.detail_ok_percentage ?? 0)}
              </Tag>
            </Space>
          ),
        },
      ]
    },
    {
      title: 'Unit',
      align: 'center' as const,
      children: [
        {
          title: (
            <Space size={4}>
              <span>Target vs Realisasi</span>
              <Tooltip title="Jumlah unit properti yang sukses terjual (approved) oleh sales agent dibandingkan target penjualan unit individunya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'unit_realization',
          align: 'center' as const,
          width: 100,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" align="center" size={0} style={{ width: '100%' }}>
              <Text>{`${formatNumber(record.unit.realization)} / ${formatNumber(record.unit.target)}`}</Text>
              <Progress
                percent={record.unit.percentage}
                size="small"
                strokeColor="#1890ff"
                style={{ width: 70, margin: 0 }}
              />
            </Space>
          ),
        },
      ]
    },
    {
      title: 'Omzet',
      align: 'center' as const,
      children: [
        {
          title: (
            <Space size={4}>
              <Text strong style={{ color: sortBy === 'omzet' ? '#1890ff' : 'inherit' }}>
                Target vs Realisasi
              </Text>
              <Tooltip title="Total nilai transaksi riil dari pemesanan unit sales agent yang disetujui (approved) dibandingkan target nilai nominal penjualan individunya.">
                <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
              </Tooltip>
            </Space>
          ),
          key: 'omzet_realization',
          align: 'center' as const,
          width: 140,
          sorter: true,
          sortOrder: getAntdSortOrder('omzet'),
          showSorterTooltip: false,
          render: (record: SalesPerformance) => (
            <Space direction="vertical" align="center" size={0} style={{ width: '100%' }}>
              <Text strong>{formatRupiah(record.omzet.realization)}</Text>
              <Progress
                percent={record.omzet.percentage}
                size="small"
                strokeColor="#7BAA18"
                style={{ width: 90, margin: 0 }}
              />
            </Space>
          ),
        },
      ]
    },
    {
      title: (
        <Space size={4}>
          <span>Rasio Konversi</span>
          <Tooltip title="Mengukur kemahiran sales agent dalam mengonversi calon pembeli menjadi transaksi sukses. Rumus: Unit Terjual (Approved) / Total Leads Ditugaskan * 100%.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'conversion_rate',
      key: 'conversion_rate',
      align: 'center' as const,
      width: 100,
      render: (rate: number) => <Text strong style={{ color: '#1890ff' }}>{formatPercent(rate)}</Text>,
    },
    {
      title: (
        <Space size={4}>
          <span>SLA Compliance</span>
          <Tooltip title="Persentase ketepatan waktu respon individu sales agent dalam melayani tindak lanjut leads baru yang ditugaskan kepada mereka.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '11px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'sla_compliance_rate',
      key: 'sla_compliance_rate',
      align: 'center' as const,
      width: 100,
      render: (rate: number) => (
        <Badge
          count={formatPercent(rate)}
          style={{
            backgroundColor: rate >= 85 ? '#52c41a' : rate >= 70 ? '#faad14' : '#ff4d4f',
            borderRadius: '4px',
            padding: '0 8px',
            fontSize: '11px',
          }}
        />
      ),
    },
  ];

  return (
    <div style={glassTableContainerStyle} className="glass-table-container">
      {/* Scoped Style for Crisp & Clean Glassmorphism Table Borders & Text Wrapping */}
      <style>{`
        .sales-glass-table.ant-table-wrapper .ant-table.ant-table-bordered > .ant-table-container {
          border: 1px solid rgba(203, 213, 225, 0.8) !important;
          border-radius: 8px;
          overflow: hidden;
        }
        .sales-glass-table .ant-table-cell {
          border-color: rgba(226, 232, 240, 0.85) !important;
        }
        .sales-glass-table .ant-table-thead > tr > th {
          border-bottom: 1px solid rgba(203, 213, 225, 0.85) !important;
          border-inline-end: 1px solid rgba(203, 213, 225, 0.75) !important;
        }
        .sales-glass-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(226, 232, 240, 0.85) !important;
          border-inline-end: 1px solid rgba(226, 232, 240, 0.75) !important;
        }
        .sales-glass-table .ant-table-cell-fix-left-last {
          border-inline-end: 1px solid rgba(203, 213, 225, 0.9) !important;
        }
        .sales-glass-table .ant-tag {
          white-space: normal !important;
          word-break: break-word !important;
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <Space size={6} direction="vertical" style={{ width: 'auto' }}>
          <Space size={6}>
            <Title level={4} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Peringkat & Performa Sales Agent
            </Title>
            <Tooltip title="Tabel kartu rapor digital komparatif untuk meninjau secara objektif produktivitas (omzet, unit, leads, konversi) and kedisiplinan (SLA) masing-masing sales agent individu.">
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '13px', cursor: 'pointer' }} />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', margin: 0 }}>
            Urutan aktif: <Text strong style={{ color: '#1890ff' }}>{getSortingLabel()}</Text>
          </Text>
        </Space>
        <Space size={12} style={{ flexWrap: 'wrap' }}>
          <Input.Search
            placeholder="Cari nama sales..."
            onSearch={onSearch}
            allowClear
            style={{ width: 220 }}
          />
          <Tag color="blue">{total} Agen</Tag>
        </Space>
      </div>
      <Table
        dataSource={performanceBySales}
        columns={salesColumns}
        rowKey="user_id"
        pagination={{
          current: page,
          pageSize: limit,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '20', '50'],
        }}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => {
            navigate(`/performa/sales-detail/${record.user_id}`);
          },
          style: { cursor: 'pointer' }
        })}
        bordered
        size="middle"
        style={{ background: 'transparent' }}
        className="glass-table sales-glass-table"
        scroll={{ x: 1600 }}
        loading={loading}
      />
    </div>
  );
};

export default SalesPerformanceTable;
